import { IconWarning, colors } from '@cabonline/ui-shared';
import React, { useCallback, useMemo } from 'react';
import { GroupBase, OptionProps, SingleValueProps } from 'react-select';
import ReactAsyncSelect, {
  AsyncProps as ReactAsyncProps,
} from 'react-select/async';

import { StyledRequired } from '../../form';
import { HelpText } from '../../input';
import { Label } from '../../label';
import {
  CustomInput,
  CustomOption,
  CustomSingleValue,
  OwnProps,
} from '../common';
import {
  Container,
  ErrorText,
  ErrorWrap,
  getStyles,
  getThemeConfig,
} from '../styles';

export type AsyncSelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = ReactAsyncProps<Option, IsMulti, Group> & OwnProps;

export const AsyncSelect = <
  Option extends object,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: AsyncSelectProps<Option, IsMulti, Group>
): React.ReactElement => {
  const { id, label, help, hasError, isRequired, testID, sentenceCased } =
    props;
  const styles = getStyles<Option, IsMulti, Group>(hasError);

  const SingleValue = useCallback(
    (svProps: SingleValueProps<Option>) => <CustomSingleValue {...svProps} sentenceCased={sentenceCased} />,
    [sentenceCased]
  );

  const renderOption = useCallback(
    (optionProps: OptionProps<Option, IsMulti, Group>) =>
      CustomOption(optionProps, testID, sentenceCased),
    [testID, sentenceCased]
  );

  const componentsConfig = useMemo(
    () => ({ Input: CustomInput, SingleValue, Option: renderOption }),
    [SingleValue, renderOption]
  );

  return (
    <Container className="react-select-container" data-testid={testID}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {isRequired && <StyledRequired aria-hidden />}
        </Label>
      )}
      {help && <HelpText>{help}</HelpText>}
      {hasError && typeof hasError === 'string' && (
        <ErrorWrap>
          <IconWarning color={colors.red} />
          <ErrorText>{hasError}</ErrorText>
        </ErrorWrap>
      )}
      <ReactAsyncSelect
        {...props}
        styles={styles}
        theme={getThemeConfig()}
        id={`${id}-input-container`}
        inputId={id}
        components={componentsConfig}
      />
    </Container>
  );
};
